import { EnsureCampaignDataOptions, getDefaultEnsureCampaignDataOptions } from "../data/models/CampaignObjects";
import { TaskStatus } from "../data/models/Tasks";
import { RequestManager } from "../RequestManager";
import AccountDataService from "../services/data/AccountDataService";
import EventService from "../services/EventService";
import { getDefaultTrackingDataOptions, TrackingDataOptions } from "../types/TrackingDataOptions";
import { getInnerError, handleResponseErrorNotifications } from "../utils/adaya/AdayaUtils";
import { getLogger } from "../utils/logging";

const { log, warn } = getLogger('PageHelper', { enabled: true });

// TODO: maybe it's better to have one global RequestManager for all pages, so they can share/pool requests and redundant ones can be avoided, I think.

/* Does this:
    -takes in request params, manages the request by key.
    -catches errors and sends to main notificaton handler/service.
    -automatically listens for special app events to clear the requests.
*/

const isErrorStatus = (status) => {
    return status && (status == TaskStatus.FinishedWithErrors || status == TaskStatus.Error);
}

export class PageHelper {

    public requestManager: RequestManager;

    constructor(requestManager?) {
        this.requestManager = requestManager || new RequestManager();

        // clear requests on login/logout, as a precaution.
        EventService.subscribe(AppEvents.LOGIN, this.reset);
        EventService.subscribe(AppEvents.LOGOUT, this.reset);
    }

    /**
     * @description Handles managing a request for campaign data.
     * Will retry a least once if any providers fail, without them.
     * Returns the last-tried ManagedRequest object.
     * @param {*} filter
     * @param {*} [overrideOpts]
     * @return {ManagedRequest} The object that handled this request.
     */
    public getDailyCampaignDataRequest = async (filter, overrideOpts?) => {
        try {
            console.log(`getDailyCampaignDataRequest `, filter)
            if (!filter) throw "No filter given to getDailyCampaignData";
            if (!filter?.timeRange) throw "No timeRange given to getDailyCampaignData filter.";
            let r, loadErrors;

            // default request options for all pages (retry on limits, waiting ~2s beteen each wait)
            let opts: EnsureCampaignDataOptions = getDefaultEnsureCampaignDataOptions(overrideOpts);

            // let page's default to not resolving IDs, or fetching new data, unless they explicitly ask to:
            opts.resolveUnknownObjects = (overrideOpts && typeof overrideOpts.resolveUnknownObjects != 'undefined' ? overrideOpts.resolveUnknownObjects : false);

            // page's by default shouldn't ask the server for new data
            opts.dontFetchNewData = (overrideOpts && typeof overrideOpts.dontFetchNewData != 'undefined' ? overrideOpts.dontFetchNewData : true);

            const tryRequest = async (forceNew = false) => {
                try {
                    // todo: look into ProviderStates to see which are not errored/enabled.
                    if (loadErrors && filter.providers) {
                        filter.providers = filter.providers.filter(p => (loadErrors.filter(e => e.provider == p).length == 0));
                        console.log(`Omitting errored providers...`, loadErrors.map(e => e.provider || e), '=>', filter.providers)
                    }

                    log(`getDailyCampaignDataRequest REQUEST:`, filter, opts);
                    r = await this.requestManager.request(
                        filter,
                        async () => AccountDataService.getDailyCampaignData(filter, opts),
                        {
                            params: filter,
                            forceNewRequest: (
                                forceNew || // force new
                                opts.forceUpdate ||  // force backend to update/ask provider's for new data
                                !opts.dontFetchNewData // tell backend to not ask provider's for new data, if they needed to.
                            )
                        }
                    );
                    log(`getDailyCampaignDataRequest RESPONSE:`, r);

                    // page-level error notification backup in case errors weren't handled through exceptions.
                    if (isErrorStatus(r?.status)) {
                        // main task failed:
                        loadErrors = handleResponseErrorNotifications(r.response);
                        return { status: r?.status, errors: loadErrors }
                    } else if (isErrorStatus(r?.response?.status)) {
                        // inner task failed
                        loadErrors = handleResponseErrorNotifications(r.response.result?.failed || r.response.result);
                        return { status: r?.response.status, errors: loadErrors }
                    }

                    return r;
                } catch (e) {
                    log(`Exception`, e);
                    loadErrors = handleResponseErrorNotifications(e);
                }
            }

            // try first time
            r = await tryRequest();
            //log(`getDailyCampaignDataRequest`, opts, ' response=>', r);

            // try second time
            if (isErrorStatus(r?.status) && opts.retryFailedRequest) {
                log(`getDailyCampaignData failed, response:`, r, `\nerrors:`, loadErrors);

                if (loadErrors) {
                    console.log(`Retrying request... 🙏`);
                    // change options to not clear data on the retry (not ideal solution but helps).
                    opts.clearDataBefore = false;
                    r = await tryRequest(true);
                } else {
                    // no error produced or not produced/not successful response
                    throw getInnerError(r);
                }
            }

            return r;
        } catch (e) {
            throw e;
        }
    }

    /**
     * @description New version of above getDailyCampaignDataRequest,
     * tailored for retrieving tracking data grouped by a custom parameter.
     * @param {*} filter
     * @param {*} [overrideOpts]
     * @return {ManagedRequest} The object that handled this request.
     */
    public getDailyTrackingDataRequest = async (account, filter, overrideOpts?) => {
        try {
            if (!filter) throw "No filter given to getDailyTrackingDataRequest";
            if (!filter?.timeRange) throw "No timeRange given to getDailyTrackingDataRequest filter.";
            if (!filter?.groupBy) throw "No groupBy given to getDailyTrackingDataRequest filter.";
            let r, loadErrors;

            // default request options for all pages (retry on limits, waiting ~2s beteen each wait)
            let opts: TrackingDataOptions = getDefaultTrackingDataOptions(overrideOpts);
            opts.dontFetchNewData = (overrideOpts && typeof overrideOpts.dontFetchNewData != 'undefined' ? overrideOpts.dontFetchNewData : true);

            const tryRequest = async (forceNew = false) => {
                try {
                    log(`getDailyTrackingDataRequest REQUEST:`, filter, opts);
                    r = await this.requestManager.request(
                        filter,
                        async () => AccountDataService.getDailyTrackingData(account, filter, opts),
                        {
                            params: filter,
                            forceNewRequest: (
                                forceNew || // force new
                                opts.forceUpdate ||  // force backend to update/ask provider's for new data
                                !opts.dontFetchNewData // tell backend to not ask provider's for new data, if they needed to.
                            )
                        }
                    );
                    log(`getDailyTrackingData RESPONSE:`, r);

                    // page-level error notification backup in case errors weren't handled through exceptions.
                    if (isErrorStatus(r?.status)) {
                        // main task failed
                        loadErrors = handleResponseErrorNotifications(r.response);
                        return { status: r?.status, errors: loadErrors }
                    } else if (isErrorStatus(r?.response?.status)) {
                        // inner task failed
                        loadErrors = handleResponseErrorNotifications(r.response.result?.failed || r.response.result);
                        return { status: r?.response.status, errors: loadErrors }
                    }

                    return r;
                } catch (e) {
                    log(`Exception`, e);
                    loadErrors = handleResponseErrorNotifications(e);
                }
            }

            // try first time
            r = await tryRequest();

            // try second time
            if (isErrorStatus(r?.status) && opts.retryFailedRequest) {
                log(`getDailyTrackingDataRequest failed, response:`, r, `\nerrors:`, loadErrors);

                if (loadErrors) {
                    console.log(`Retrying request... 🙏`);
                    // change options to not clear data on the retry (not ideal solution but helps).
                    opts.clearDataBefore = false;
                    r = await tryRequest(true);
                } else {
                    // no error produced or not produced/not successful response
                    throw getInnerError(r);
                }
            }

            return r;
        } catch (e) {
            throw e;
        }
    }

    // Convenience method to pass to underlying RequestManager, since it's the only other method clients will use.
    public requestMatchesKey = (request: ManagedRequest, key: string | {}) => {
        return this.requestManager.requestMatchesKey(request, key);
    }

    private reset = () => {
        this.requestManager = new RequestManager();
    }

}